import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  DialogContentText,
  Box,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import useDebounce from '../../hooks/useDebounce';
import DeleteDialog from '../common/DeleteDialog';
import { useUI } from '../../context/UIContext';
import { useRegisterModalBackHandler } from '../../context/ModalBackHandlerContext';
import { useResourceState, useResourceDispatch } from '../../context/ResourceContext';
import { useAuth } from '../../context/AuthContext';
import { getContentTypeConfig } from '../content/ContentTypeRegistry';
import ScrollIndicatorWrapper from '../common/ScrollIndicatorWrapper';

const DomainObjectModal = ({ open, onClose, onActionSuccess, itemToEdit, config, sectionTitle, onContainedItemCreated, initialMode, ...passedAdditionalProps }) => {
  const theme = useTheme();
  const {
    singularName,
    api,
    FormComponent,
    ViewComponent,
    getInitialState,
    getDisplayName,
    getDeleteWarning,
    creatableFields,
    createHeaderTitle,
    createHeaderDescription,
    icon: ConfigIcon,
    modalSize = 'md',
    scrollIndicator = true,
  } = config;

  const { user } = useAuth();
  const { getResourceById } = useResourceState();
  const { upsertResource } = useResourceDispatch();
  
  const liveItem = itemToEdit ? getResourceById(itemToEdit.id) : null;
  const itemForDisplay = liveItem || itemToEdit;

  const [itemData, setItemData] = useState(null);
  const [itemId, setItemId] = useState(null);
  const [mode, setMode] = useState('view');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [saveState, setSaveState] = useState('idle');
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [isHydrating, setIsHydrating] = useState(false);
  const [hydrationError, setHydrationError] = useState(null);
  const [modalOverrides, setModalOverrides] = useState({ title: null, actions: null });
  const [formProps, setFormProps] = useState({}); // Dynamic props for FormComponent

  const debouncedItemData = useDebounce(itemData, 1000);
  const isEditMode = Boolean(itemToEdit || itemId);
  const originalItemDataRef = useRef(null);
  const beforeSaveRef = useRef(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createSuccessMessage, setCreateSuccessMessage] = useState('');
  const [nestedModalState, setNestedModalState] = useState({ open: false, config: null, onSuccess: null });
  const [internalAdditionalProps, setInternalAdditionalProps] = useState({});
  const [isLoadingAdditionalProps, setIsLoadingAdditionalProps] = useState(false);
  const { isMobile } = useUI();
  const internalFetchInitiated = useRef(false);
  const isAdmin = user?.role === 'admin';

  const shadowsId = itemData?.shadows_id || itemForDisplay?.shadows_id;
  const userId = itemData?.user_id || itemForDisplay?.user_id;
  const asSystem = itemData?.as_system || itemForDisplay?.as_system;
  const isSystemRecord = userId === 'system_user' || asSystem === true;
  const isShadowRecord = !!shadowsId && !isSystemRecord;

  const handleBackPress = useCallback(() => {
    if (mode === 'edit' && isEditMode) {
      setMode('view');
      setItemData(originalItemDataRef.current);
      setError(null);
      setFormProps({});
    } else {
      onClose();
    }
  }, [mode, isEditMode, onClose]);

  useRegisterModalBackHandler(handleBackPress, open);

  useEffect(() => {
    const { getAdditionalProps } = config;
    const shouldFetch = open && getAdditionalProps && Object.keys(passedAdditionalProps).length === 0;
    if (shouldFetch && !internalFetchInitiated.current) {
      internalFetchInitiated.current = true;
      const fetchExtraData = async () => {
        setIsLoadingAdditionalProps(true);
        try {
          const extraProps = await getAdditionalProps();
          setInternalAdditionalProps(extraProps);
        } catch (err) {
          setError(err.message || 'Failed to load supporting data.');
        } finally {
          setIsLoadingAdditionalProps(false);
        }
      };
      fetchExtraData();
    } else if (!open) {
      internalFetchInitiated.current = false;
      setInternalAdditionalProps({});
    }
  }, [open, config, passedAdditionalProps]);

  useEffect(() => {
    if (open) {
      setSaveState('idle');
      setError(null);
      setCreateSuccessMessage('');
      setHydrationError(null);
      beforeSaveRef.current = null;
      setModalOverrides({ title: null, actions: null });
      setFormProps({});
      
      const initializeState = (data) => {
        const initialState = getInitialState(data);
        setItemData(initialState);
        originalItemDataRef.current = initialState;
        setItemId(data?.id || null);
        const isCreatingNew = !data;
        if (isCreatingNew) {
          setMode('edit');
        } else if (initialMode) {
          setMode(initialMode);
        } else {
          setMode('view');
        }
      };

      if (itemForDisplay && itemForDisplay._isPartial) {
        setIsHydrating(true);
        const resourceType = config.resourceType || itemForDisplay.type || itemForDisplay.resource_type;
        const typeConfig = getContentTypeConfig(resourceType);
        const fetcher = typeConfig?.fetcher || config.api?.getById;
        if (fetcher) {
          fetcher(itemForDisplay.id)
            .then(fullData => {
              const merged = { ...itemForDisplay, ...fullData, _isPartial: false };
              upsertResource(merged);
              initializeState(merged);
            })
            .catch(err => {
              console.error("[DomainObjectModal] Hydration failed:", err);
              setHydrationError("Failed to load full details. Showing partial data.");
              initializeState(itemForDisplay);
            })
            .finally(() => {
              setIsHydrating(false);
            });
        } else {
          initializeState(itemForDisplay);
          setIsHydrating(false);
        }
      } else {
        initializeState(itemForDisplay);
        setIsHydrating(false);
      }
    } else {
      setItemData(null);
      originalItemDataRef.current = null;
      setItemId(null);
      setError(null);
      setSaveState('idle');
      setMode('view');
      setIsHydrating(false);
      setHydrationError(null);
      setModalOverrides({ title: null, actions: null });
      setFormProps({});
    }
  }, [itemToEdit, open, getInitialState, initialMode, config, upsertResource]);

  useEffect(() => {
    if (open && mode === 'view' && liveItem && !liveItem._isPartial) {
      const liveItemState = getInitialState(liveItem);
      if (JSON.stringify(liveItemState) !== JSON.stringify(itemData)) {
        setItemData(liveItemState);
        originalItemDataRef.current = liveItemState;
      }
    }
  }, [liveItem, mode, open, getInitialState, itemData]);

  useEffect(() => {
    let timer;
    if (saveState === 'saved') {
      timer = setTimeout(() => setSaveState('idle'), 3000);
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [saveState]);

  useEffect(() => {
    if (mode !== 'edit' || !itemId || !debouncedItemData || !getDisplayName(debouncedItemData).trim() || JSON.stringify(debouncedItemData) === JSON.stringify(originalItemDataRef.current)) {
      return;
    }
    if (beforeSaveRef.current) return;
    const autoSave = async () => {
      setSaveState('saving');
      setError(null);
      try {
        const updatedItem = await api.update(itemId, debouncedItemData);
        originalItemDataRef.current = debouncedItemData;
        upsertResource(updatedItem);
        setSaveState('saved');
      } catch (err) {
        console.error(`Error auto-saving ${singularName}:`, err);
        setError(err.message || `Failed to save changes.`);
        setSaveState('error');
      }
    };
    autoSave();
  }, [debouncedItemData, itemId, upsertResource, api, singularName, getDisplayName, mode]);

  const registerBeforeSave = useCallback((callback) => {
    beforeSaveRef.current = callback;
  }, []);

  const handleActionInternal = useCallback((action, payload) => {
    if (action === 'request_edit_mode') {
      setMode('edit');
      if (payload?.focusActivityId) {
        setFormProps({ initialActivityId: payload.focusActivityId });
      }
      return;
    }
    onActionSuccess(action, payload);
  }, [onActionSuccess]);

  const handleCreate = async () => {
    let dataToSave = itemData;
    if (beforeSaveRef.current) {
      try {
        const interceptedData = await beforeSaveRef.current();
        if (interceptedData) dataToSave = interceptedData;
      } catch (e) {
        console.error("Pre-save check failed:", e);
      }
    }
    if (!dataToSave || !getDisplayName(dataToSave).trim()) {
      setError(`${singularName} Name is a required field.`);
      return;
    }
    setIsCreating(true);
    setError(null);
    setCreateSuccessMessage('');
    try {
      const newItem = await api.create(dataToSave);
      setCreateSuccessMessage(`${singularName} '${getDisplayName(newItem)}' created successfully.`);
      const newInitialState = getInitialState(null);
      setItemData(newInitialState);
      originalItemDataRef.current = newInitialState;
      onActionSuccess('create', newItem);
      setTimeout(() => setCreateSuccessMessage(''), 4000);
    } catch (err) {
      console.error(`Error creating ${singularName}:`, err);
      setError(err.message || `Failed to create ${singularName}.`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleConfirmDelete = async () => {
    const idToDelete = itemForDisplay?.id || itemId;
    if (!idToDelete) return;
    const isRevertOperation = isShadowRecord;
    setIsDeleting(true);
    setError(null);
    try {
      await api.delete(idToDelete);
      onActionSuccess(isRevertOperation ? 'revert' : 'delete', { id: idToDelete });
      setIsDeleteConfirmationOpen(false);
      onClose();
    } catch (err) {
      setError(err.message || `Failed to delete the ${singularName}.`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFieldChange = useCallback((field, value) => {
    setSaveState(current => current === 'saved' ? 'idle' : current);
    setItemData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDataSet = useCallback((newData) => {
    setSaveState(current => current === 'saved' ? 'idle' : current);
    setItemData(newData);
  }, []);

  const handleDoneEditing = async () => {
    if (saveState === 'saving') return;
    let dataToSave = itemData;
    if (beforeSaveRef.current) {
      try {
        setSaveState('saving');
        const interceptedData = await beforeSaveRef.current();
        if (interceptedData) {
          dataToSave = interceptedData;
          setItemData(interceptedData);
        }
      } catch (e) {
        console.error("Pre-save check failed:", e);
        setError("Failed to process pending changes.");
        setSaveState('error');
        return;
      }
    }
    const hasPendingChanges = JSON.stringify(dataToSave) !== JSON.stringify(originalItemDataRef.current);
    const canSave = mode === 'edit' && itemId && getDisplayName(dataToSave).trim();
    if (!canSave || !hasPendingChanges) {
      setMode('view');
      setSaveState('idle');
      setFormProps({});
      return;
    }
    setSaveState('saving');
    setError(null);
    try {
      const updatedItem = await api.update(itemId, dataToSave);
      originalItemDataRef.current = dataToSave;
      onActionSuccess('update', updatedItem);
      setSaveState('saved');
      setMode('view');
      setFormProps({});
    } catch (err) {
      console.error(`Error saving ${singularName} on 'Done':`, err);
      setError(err.message || `Failed to save changes.`);
      setSaveState('error');
    }
  };

  const handleOpenNestedCreateModal = (configForNewItem, onSuccessCallback) => {
    setNestedModalState({ open: true, config: configForNewItem, onSuccess: onSuccessCallback });
  };

  const handleNestedModalClose = () => {
    setNestedModalState({ open: false, config: null, onSuccess: null });
  };

  const handleNestedModalSuccess = (action, newItem) => {
    if (action === 'create') {
      if (nestedModalState.onSuccess) {
        nestedModalState.onSuccess(newItem);
      }
      if (onContainedItemCreated) {
        onContainedItemCreated();
      }
      handleNestedModalClose();
    }
  };

  const SaveStatus = () => {
    const statusMap = {
      saving: { icon: <CircularProgress size={16} sx={{ mr: 1 }} />, text: 'Saving...' },
      saved: { icon: <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />, text: 'All changes saved' },
      error: { icon: <ErrorOutlineIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />, text: 'Save failed' },
    };
    const currentStatus = statusMap[saveState];
    if (!currentStatus) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', typography: 'caption' }}>
        {currentStatus.icon}
        {currentStatus.text}
      </Box>
    );
  };

  if (!itemData && !isHydrating) return null;
  const additionalProps = Object.keys(passedAdditionalProps).length > 0 ? passedAdditionalProps : internalAdditionalProps;
  const isCreateMode = mode === 'edit' && !isEditMode;
  const displayTitle = isCreateMode && createHeaderTitle
    ? createHeaderTitle
    : (isEditMode && mode === 'view' ? sectionTitle || singularName : isEditMode ? `Edit ${singularName}` : `Add New ${singularName}`);
  const displayDescription = isCreateMode && createHeaderDescription
    ? createHeaderDescription
    : (isCreateMode ? `Define a new ${singularName.toLowerCase()} for your account below.` : null);
  const showFooterCreateButton = !isEditMode && config.id !== 'workoutLogs';

  let deleteButtonConfig = null;
  if (isSystemRecord) {
    if (isAdmin) {
      deleteButtonConfig = {
        label: 'Delete System Record',
        icon: <DeleteIcon />,
        color: 'error',
        title: 'Delete System Record',
        overrideContent: (
          <>
            Are you sure you want to delete this <strong>System Record</strong>?
            <br/><br/>
            <Alert severity="error">
              <strong>Warning:</strong> This will delete the base record for ALL users. This action cannot be undone.
            </Alert>
          </>
        )
      };
    }
  } else if (isShadowRecord) {
    deleteButtonConfig = {
      label: 'Revert',
      icon: <RestoreIcon />,
      color: 'warning',
      title: 'Revert to Default',
      confirmLabel: 'Revert',
      confirmColor: 'warning',
      overrideContent: (
        <DialogContentText>Are you sure you want to revert your changes?<br/><br/>This will delete your customized version and restore the original <strong>System Default</strong> record.</DialogContentText>
      )
    };
  } else {
    deleteButtonConfig = {
      label: 'Delete',
      icon: <DeleteIcon />,
      color: 'error',
      title: 'Confirm Deletion'
    };
  }

  const ResolvedIcon = typeof ConfigIcon === 'function' ? ConfigIcon(itemData) : ConfigIcon;
  const isScrollEnabled = scrollIndicator !== false;
  const scrollProps = typeof scrollIndicator === 'object' ? scrollIndicator : {};

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={modalSize}
        fullScreen={isMobile}
        aria-labelledby="domain-object-modal-title"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            maxHeight: isMobile ? '100%' : '90vh',
            display: 'flex',
            flexDirection: 'column'
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            pb: 2,
            flexShrink: 0,
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 50%, ${theme.palette.background.paper} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 50%, #ffffff 100%)`,
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'relative'
          }}
        >
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary', zIndex: 10 }}
          >
            <CloseIcon />
          </IconButton>
          {modalOverrides.title ? (
            modalOverrides.title
          ) : (
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={2}>
                {ResolvedIcon && (
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                      color: 'primary.main',
                      display: 'flex',
                    }}
                  >
                    <ResolvedIcon fontSize="medium" />
                  </Box>
                )}
                <Typography variant="h5" sx={{ fontWeight: 700 }} id="domain-object-modal-title">
                  {displayTitle}
                </Typography>
              </Stack>
              {displayDescription && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: ResolvedIcon ? 7 : 0 }}>
                  {displayDescription}
                </Typography>
              )}
            </Stack>
          )}
        </Box>
        <DialogContent
          sx={{
            p: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          }}
        >
          <ScrollIndicatorWrapper
            disabled={!isScrollEnabled}
            containerSx={{ flexGrow: 1, minHeight: 0 }}
            sx={{ p: { xs: 2, sm: 2, md: 3 } }}
            resetScrollDeps={[mode, itemId, modalOverrides?.scrollKey]}
            {...scrollProps}
          >
            {error && !isDeleteConfirmationOpen && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {hydrationError && <Alert severity="warning" sx={{ mb: 2 }}>{hydrationError}</Alert>}
            {createSuccessMessage && <Alert severity="success" sx={{ mb: 2 }}>{createSuccessMessage}</Alert>}
            {isHydrating || isLoadingAdditionalProps ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress />
                <Typography sx={{mt: 2}} color="text.secondary">Loading details...</Typography>
              </Box>
            ) : (
              mode === 'view' && ViewComponent ? (
                <Box sx={{ py: { xs: 1, sm: 2 } }}>
                  <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}>
                    <ViewComponent
                      itemData={itemData}
                      {...additionalProps}
                      onActionSuccess={handleActionInternal}
                      onClose={onClose}
                    />
                  </Suspense>
                </Box>
              ) : (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}>
                  <FormComponent
                    formData={itemData}
                    {...additionalProps}
                    {...formProps}
                    onFieldChange={handleFieldChange}
                    onDataSet={handleDataSet}
                    getInitialState={getInitialState}
                    disabled={isDeleting || isCreating}
                    saveState={saveState}
                    isEditMode={isEditMode}
                    creatableFields={creatableFields}
                    onOpenCreateModal={handleOpenNestedCreateModal}
                    onContainedItemCreated={onContainedItemCreated}
                    onActionSuccess={handleActionInternal}
                    isAdmin={isAdmin}
                    registerBeforeSave={registerBeforeSave}
                    setModalOverrides={setModalOverrides}
                    onClose={onClose}
                  />
                </Suspense>
              )
            )}
          </ScrollIndicatorWrapper>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: { xs: 1, sm: 2 }, px: { xs: 2, sm: 3 }, flexShrink: 0, borderTop: '1px solid', borderColor: 'divider' }}>
          {modalOverrides.actions ? (
            modalOverrides.actions
          ) : (
            <>
              <Box>
                {isEditMode && deleteButtonConfig && (
                  <Button
                    onClick={() => setIsDeleteConfirmationOpen(true)}
                    color={deleteButtonConfig.color}
                    startIcon={deleteButtonConfig.icon}
                    disabled={isDeleting}
                  >
                    {deleteButtonConfig.label}
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {isEditMode ? (
                  mode === 'edit' ? (
                    <>
                      <SaveStatus />
                      <Button variant="outlined" onClick={handleDoneEditing} startIcon={<DoneIcon />} disabled={saveState === 'saving'}>
                        Done Editing
                      </Button>
                    </>
                  ) : (
                    <Button variant="outlined" onClick={() => setMode('edit')} startIcon={<EditIcon />}>
                      Edit
                    </Button>
                  )
                ) : (
                  showFooterCreateButton && (
                    <Button variant="contained" onClick={handleCreate} disabled={isCreating} startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : null}>
                      {isCreating ? 'Creating...' : 'Create'}
                    </Button>
                  )
                )}
              </Box>
            </>
          )}
        </DialogActions>
      </Dialog>
      {nestedModalState.open && (
        <DomainObjectModal
          open={nestedModalState.open}
          onClose={handleNestedModalClose}
          onActionSuccess={handleNestedModalSuccess}
          itemToEdit={null}
          config={nestedModalState.config}
          sectionTitle={nestedModalState.config.pluralName}
        />
      )}
      <DeleteDialog
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={itemData ? getDisplayName(itemData) : ''}
        itemType={singularName.toLowerCase()}
        isDeleting={isDeleting}
        error={error}
        title={deleteButtonConfig?.title}
        confirmLabel={deleteButtonConfig?.confirmLabel}
        confirmColor={deleteButtonConfig?.confirmColor}
        overrideContent={deleteButtonConfig?.overrideContent}
      >
        {!deleteButtonConfig?.overrideContent && getDeleteWarning && <DialogContentText sx={{mt: 1}}>{getDeleteWarning()}</DialogContentText>}
      </DeleteDialog>
    </>
  );
};

DomainObjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onActionSuccess: PropTypes.func.isRequired,
  itemToEdit: PropTypes.object,
  config: PropTypes.object.isRequired,
  sectionTitle: PropTypes.string,
  onContainedItemCreated: PropTypes.func,
  initialMode: PropTypes.oneOf(['view', 'edit']),
};

export default DomainObjectModal;